import MainLayout from "@/layouts/MainLayout";

const PrivacyPolicy = () => {
  return (
    <MainLayout
      title="Privacy Policy - Niumora"
      description="Our privacy policy outlines how we collect, use, and protect your personal information."
      keywords="privacy policy, data protection, personal information, Niumora"
      ogType="article"
    >
      <div className="container max-w-4xl mx-auto px-4 py-16 md:py-24">
        <h1 className="text-4xl font-bold mb-8">Privacy Policy for Niumora</h1>
        
        <div className="prose prose-slate max-w-none">
          <p className="text-lg mb-8">Effective Date: March 29, 2025</p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
            <p>
              Welcome to Niumora. We are committed to protecting your privacy and ensuring that your personal data is handled in a safe and responsible manner. This Privacy Policy outlines how we collect, use, and safeguard your information when you visit our website <a href="https://niumora.com/" className="text-primary hover:underline">https://niumora.com/</a> and engage with our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">2. Data Controller</h2>
            <p>
              Niumora Ltd. is the data controller responsible for your personal data. If you have any questions about this Privacy Policy or our data practices, please contact us at:{" "}
              <a href="mailto:privacy@niumora.com" className="text-primary hover:underline">privacy@niumora.com</a>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">3. Information We Collect</h2>
            <p className="mb-4">We may collect and process the following personal data:</p>
            <ul className="list-disc pl-6 mb-4">
              <li><strong>Personal Identification Information:</strong> Name, email address, phone number, job title.</li>
              <li><strong>Company Information:</strong> Company name, company website address.</li>
              <li><strong>Technical Data:</strong> IP address, browser type and version, time zone setting, browser plug-in types, operating system, and platform.</li>
              <li><strong>Usage Data:</strong> Information about how you use our website, products, and services.</li>
              <li><strong>Marketing and Communications Data:</strong> Your preferences in receiving marketing from us and your communication preferences.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">4. How We Collect Your Data</h2>
            <p className="mb-4">We collect data through:</p>
            <ul className="list-disc pl-6 mb-4">
              <li><strong>Direct Interactions:</strong> When you fill out forms on our website, subscribe to our services, or contact us directly.</li>
              <li><strong>Automated Technologies:</strong> As you interact with our website, we may automatically collect Technical Data using cookies and similar technologies. [Refer to our Cookie Policy for more details.]</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">5. Purpose and Legal Basis for Processing</h2>
            <p className="mb-4">We process your personal data for the following purposes:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>To register you as a new customer, manage your account, and deliver our services.</li>
              <li>To administer and protect our business and website (including troubleshooting, data analysis, testing, system maintenance, support, reporting, and hosting of data).</li>
              <li>To make suggestions and recommendations to you about goods or services that may be of interest.</li>
            </ul>
            <p className="mb-4">The legal bases for processing your data include:</p>
            <ul className="list-disc pl-6 mb-4">
              <li><strong>Performance of a Contract</strong> – when processing is necessary to fulfil a contract with you.</li>
              <li><strong>Legal Obligation</strong> – when we are legally required to process your data.</li>
              <li><strong>Legitimate Interests</strong> – when processing is necessary for our legitimate business interests.</li>
              <li><strong>Consent</strong> – when you have given clear permission for us to process your data.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">6. Data Sharing and Disclosure</h2>
            <p className="mb-4">We do not sell or rent your personal data. However, we may share your information with:</p>
            <ul className="list-disc pl-6 mb-4">
              <li><strong>Service Providers:</strong> Third parties who provide IT and system administration services.</li>
              <li><strong>Professional Advisers:</strong> Including lawyers, auditors, and consultants.</li>
              <li><strong>Regulatory Authorities:</strong> HMRC, regulators, and other authorities as required by law.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">7. International Data Transfers</h2>
            <p>
              If we transfer your personal data outside the UK or European Economic Area (EEA), we will ensure an adequate level of protection through appropriate safeguards such as:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>Standard Contractual Clauses approved by the European Commission</li>
              <li>Transfers to countries deemed to provide an adequate level of protection by the UK government</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">8. Data Security</h2>
            <p>
              We implement appropriate technical and organisational security measures to protect your personal data from unauthorised access, use, alteration, or disclosure. Access to your data is limited to employees, agents, contractors, and third parties who have a legitimate business need to know.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">9. Data Retention</h2>
            <p>
              We will retain your personal data only for as long as necessary to fulfil the purposes we collected it for, including satisfying legal, accounting, or reporting requirements. Retention periods vary depending on the nature of the data and legal obligations.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">10. Your Legal Rights</h2>
            <p className="mb-4">Under data protection law, you have rights including:</p>
            <ul className="list-disc pl-6 mb-4">
              <li><strong>Right to Access:</strong> Request copies of your personal data.</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete data.</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your personal data in certain circumstances.</li>
              <li><strong>Right to Restriction:</strong> Request limited processing of your data.</li>
              <li><strong>Right to Object:</strong> Object to processing based on legitimate interests or direct marketing.</li>
              <li><strong>Right to Data Portability:</strong> Request transfer of your data to another organisation or to you.</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at{" "}
              <a href="mailto:privacy@niumora.com" className="text-primary hover:underline">privacy@niumora.com</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in law or our practices. Updates will be posted on this page, and we encourage you to review it periodically. The revised version will be effective from the date it is posted.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">12. Complaints</h2>
            <p className="mb-4">
              If you have any concerns about our use of your personal data, please contact us at{" "}
              <a href="mailto:privacy@niumora.com" className="text-primary hover:underline">privacy@niumora.com</a>. 
              If you are not satisfied with our response, you have the right to lodge a complaint with the Information Commissioner's Office (ICO), the UK supervisory authority for data protection issues:
            </p>
            <ul className="list-none pl-6 mb-4">
              <li><strong>Website:</strong>{" "}<a href="https://www.ico.org.uk" className="text-primary hover:underline">www.ico.org.uk</a></li>
              <li><strong>Phone:</strong> 0303 123 1113</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">13. Cookies and Tracking Technologies</h2>
            <p className="mb-4">
              We use cookies and similar tracking technologies on our website{" "}
              <a href="https://niumora.com" className="text-primary hover:underline">https://niumora.com</a>{" "}
              to enhance your browsing experience, analyse site traffic, and personalise content.
            </p>

            <h3 className="text-xl font-semibold mb-3">What are Cookies?</h3>
            <p className="mb-4">
              Cookies are small text files that are stored on your device when you visit a website. They help the site recognise your device and remember certain information about your visit.
            </p>

            <h3 className="text-xl font-semibold mb-3">Types of Cookies We Use:</h3>
            <ul className="list-disc pl-6 mb-4">
              <li><strong>Essential Cookies:</strong> These are necessary for the website to function properly (e.g. for security or form submission).</li>
              <li><strong>Analytics Cookies:</strong> These allow us to understand how visitors interact with our website (e.g. Google Analytics).</li>
              <li><strong>Functionality Cookies:</strong> These help remember your preferences (e.g. language or region).</li>
              <li><strong>Marketing/Advertising Cookies:</strong> These may be used to deliver relevant ads or track effectiveness of advertising campaigns.</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">Third-Party Cookies:</h3>
            <p className="mb-4">
              We may allow third-party service providers to place cookies on your device for analytics and marketing purposes. These providers may collect data about your browsing behaviour across different websites.
            </p>

            <h3 className="text-xl font-semibold mb-3">Managing Cookies:</h3>
            <p className="mb-4">
              You can control or disable cookies through your browser settings. Most browsers allow you to:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>View what cookies are stored</li>
              <li>Block cookies from specific websites</li>
              <li>Delete all cookies</li>
            </ul>
            <p className="mb-4">
              Please note that disabling certain cookies may impact the functionality of the website.
            </p>
            <p className="mb-4">
              You can also manage cookie preferences using our <strong>Cookie Consent Banner</strong> when you first visit the site.
            </p>

            <h3 className="text-xl font-semibold mb-3">More Information:</h3>
            <p>
              For more detailed information about cookies, visit{" "}
              <a href="https://www.allaboutcookies.org" className="text-primary hover:underline">www.allaboutcookies.org</a>{" "}
              or the{" "}
              <a href="https://ico.org.uk/for-the-public/online/cookies/" className="text-primary hover:underline">ICO guidance on cookies</a>.
            </p>
          </section>
        </div>
      </div>
    </MainLayout>
  );
};

export default PrivacyPolicy;
